import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Car, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../services/api';
import BannierePublicitaire from '../components/BannierePublicitaire';

const Accueil = () => {
  const [annonces, setAnnonces] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [statsTypes, setStatsTypes] = useState({});
  const [statsMarques, setStatsMarques] = useState({});
  const [logoErrors, setLogoErrors] = useState({});
  const [heroImages, setHeroImages] = useState([
    'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=3840&q=90&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=3840&q=90&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=3840&q=90&auto=format&fit=crop'
  ]);
  const [heroIndex, setHeroIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/annonces', { params: { limit: 20 } })
      .then(res => setAnnonces(res.data.annonces))
      .catch(err => console.error(err));
    API.get('/annonces/stats/groupes')
      .then(res => {
        setStatsTypes(res.data.types || {});
        setStatsMarques(res.data.marques || {});
      })
      .catch(err => console.error(err));
    API.get('/hero/images')
      .then(res => {
        if (res.data?.images?.length) setHeroImages(res.data.images);
      })
      .catch(() => { /* fallback sur les URLs par défaut */ });
  }, []);

  useEffect(() => {
    if (heroImages.length < 2) return;
    const id = setInterval(() => {
      setHeroIndex(i => (i + 1) % heroImages.length);
    }, 60000);
    return () => clearInterval(id);
  }, [heroImages.length]);

  // ✅ 2 - Ordre défini : Moto, Citadine, Berline, SUV, 4x4, Pick-up, Utilitaire, Engin, Monospace
  const typesRapides = [
    { value: 'moto',       label: 'Moto',       labelMobile: 'Moto',    icon: 'mdi:motorbike' },
    { value: 'citadine',   label: 'Citadine',   labelMobile: 'Citadine',icon: 'mdi:car-hatchback' },
    { value: 'berline',    label: 'Berline',    labelMobile: 'Berline', icon: 'mdi:car-side' },
    { value: 'suv',        label: 'SUV',        labelMobile: 'SUV',     icon: 'mdi:car-estate' },
    { value: '4x4',        label: '4x4',        labelMobile: '4x4',     icon: 'mdi:car-lifted-pickup' },
    { value: 'pickup',     label: 'Pick-up',    labelMobile: 'Pick-up', icon: 'mdi:car-pickup' },
    { value: 'utilitaire', label: 'Utilitaire', labelMobile: 'Utilitaire',   icon: 'mdi:van-utility' },
    { value: 'engin',      label: 'Engin',      labelMobile: 'Engin',   icon: 'mdi:bulldozer' },
    { value: 'monospace',  label: 'Monospace',  labelMobile: 'Mono',    icon: 'mdi:van-passenger' },
  ];

  // ✅ 3 - Ordre défini : Toyota, Mercedes, BMW, Hyundai, KIA, Nissan, Ford, Mitsubishi, VW, Peugeot, Renault, Honda, Audi
  const marquesRapides = [
    { value: 'Toyota',     slug: 'toyota',       logoUrl: null, color: '#EB0A1E' },
    { value: 'Mercedes-Benz', slug: 'mercedes-benz', logoUrl: null, color: '#333' },
    { value: 'BMW',        slug: 'bmw',          logoUrl: null, color: '#0066B1' },
    { value: 'Hyundai',    slug: 'hyundai',      logoUrl: null, color: '#002C5F' },
    { value: 'Kia',        slug: 'kia',          logoUrl: null, color: '#05141F' },
    { value: 'Nissan',     slug: 'nissan',       logoUrl: null, color: '#C3002F' },
    { value: 'Ford',       slug: 'ford',         logoUrl: null, color: '#003478' },
    { value: 'Mitsubishi', slug: 'mitsubishi',   logoUrl: null, color: '#E60012' },
    { value: 'Volkswagen', slug: 'volkswagen',   logoUrl: null, color: '#001E50' },
    { value: 'Peugeot',    slug: 'peugeot',      logoUrl: null, color: '#1C2A38' },
    { value: 'Renault',    slug: 'renault',      logoUrl: null, color: '#FFCC00' },
    { value: 'Honda',      slug: 'honda',        logoUrl: null, color: '#CC0000' },
    { value: 'Audi',       slug: 'audi',         logoUrl: null, color: '#BB0A30' },
  ];

  const handleRecherche = (e) => {
    e.preventDefault();
    if (recherche.trim()) {
      navigate(`/annonces?recherche=${encodeURIComponent(recherche.trim())}`);
    } else {
      navigate('/annonces');
    }
  };
 
  const LogoMarque = ({ value, slug, logoUrl, color }) => {
    const total = statsMarques[value] || 0;
    const hasError = logoErrors[value];
    const src = logoUrl || `https://cdn.jsdelivr.net/gh/filippofilip95/car-logos-dataset@master/logos/optimized/${slug}.png`;
    return (
      <Link
        to={`/annonces?marque=${encodeURIComponent(value)}`}
        className="flex flex-col items-center justify-center py-1 transition group rounded-lg hover:bg-gray-50"
        title={value}
      >
        <div className="w-10 h-10 flex items-center justify-center group-hover:scale-110 transition">
          {hasError ? (
            <div style={{ background: color }} className="w-8 h-8 rounded-full flex items-center justify-center">
              <span className="text-white font-black text-xs">{value.charAt(0)}</span>
            </div>
          ) : (
            <img
              src={src}
              alt={value}
              className="w-10 h-10 object-contain"
              onError={() => setLogoErrors(prev => ({ ...prev, [value]: true }))}
            />
          )}
        </div>
        <span className="text-gray-400" style={{ fontSize: '9px' }}>{total}</span>
      </Link>
    );
  };
 
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
 
      {/* Hero + Stats */}
      <div className="relative overflow-hidden" style={{ height: "420px" }}>
        {heroImages.map((src, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity ease-in-out"
            style={{
              opacity: heroIndex === i ? 1 : 0,
              transitionDuration: '1800ms'
            }}
          >
            <img
              src={src}
              alt=""
              className="hero-kenburns absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center center" }}
            />
          </div>
        ))}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.75) 100%)'
        }} />
        <div className="relative z-10 h-full flex flex-col items-center justify-end px-4 text-center pb-6">
          <div style={{ color: '#F5A623' }} className="text-xs font-bold tracking-widest uppercase mb-3">
            La marketplace automobile premium
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3 leading-tight">
            Trouvez votre <span style={{ color: '#F5A623' }}>véhicule idéal</span> au <span style={{ color: '#F5A623' }}>GABON</span>
          </h1>
          <p className="text-gray-300 text-sm mb-6">
            Des milliers d'annonces de véhicules neufs et d'occasion
          </p>
          <form onSubmit={handleRecherche} className="w-full max-w-xl flex gap-2 mb-6">
            <input
              type="text"
              placeholder="Rechercher une marque, un modèle, un type..."
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              className="flex-1 px-4 py-3 rounded-xl text-sm focus:outline-none text-gray-800"
              style={{ background: 'rgba(255,255,255,0.95)' }}
            />
            <button
              type="submit"
              style={{ background: '#F5A623' }}
              className="text-black px-5 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition flex items-center gap-2"
            >
              <Search size={16} /> Rechercher
            </button>
          </form>
          <div className="w-full max-w-xl">
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Annonces actives', value: annonces.length + '+' },
                { label: 'Vendeurs Qualifiés', value: '100%' },
                { label: 'Satisfaction', value: '4.9/5' }
              ].map(({ label, value }) => (
                <div key={label} className="text-center">
                  <p style={{ color: '#F5A623' }} className="text-xl font-black">{value}</p>
                  <p className="text-gray-400 text-xs mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
 
      {/* ✅ Types et Marques — espace vertical compact */}
      <div className="bg-white py-1 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">

          {/* PAR TYPE */}
          <div className="mb-0.5">
            <h3 className="text-gray-700 text-xs font-bold uppercase tracking-wider mb-0.5 flex items-center gap-2">
              <span style={{ width: '3px', height: '12px', background: '#F5A623' }} className="rounded" />
              Par type
            </h3>

            {/* Desktop : 9 types */}
            <div className="hidden md:grid grid-cols-9 gap-0">
              {typesRapides.map(({ value, label, icon }) => (
                <Link key={value} to={`/annonces?type_vehicule=${encodeURIComponent(value)}`}
                  className="flex flex-col items-center justify-center py-0.5 transition group rounded-lg hover:bg-gray-50">
                  <img src={`https://api.iconify.design/${icon}.svg?color=%231f2937`} alt={label}
                    className="w-8 h-8 object-contain mb-0 group-hover:scale-110 transition" />
                  <span className="text-gray-800 font-semibold text-center group-hover:text-yellow-600 transition" style={{ fontSize: '10px' }}>{label}</span>
                  <span className="text-gray-400" style={{ fontSize: '9px' }}>{statsTypes[value] || 0}</span>
                </Link>
              ))}
            </div>

            {/* ✅ 4 - Mobile : 8 premiers types sur 1 ligne */}
            <div className="md:hidden grid gap-0" style={{ gridTemplateColumns: 'repeat(8, 1fr)' }}>
              {typesRapides.slice(0, 8).map(({ value, label, labelMobile, icon }) => (
                <Link key={value} to={`/annonces?type_vehicule=${encodeURIComponent(value)}`}
                  className="flex flex-col items-center justify-center py-0.5 transition group rounded-lg hover:bg-gray-50">
                  <img src={`https://api.iconify.design/${icon}.svg?color=%231f2937`} alt={label}
                    className="w-7 h-7 object-contain mb-0 group-hover:scale-110 transition" />
                  <span className="text-gray-800 font-semibold text-center group-hover:text-yellow-600 transition" style={{ fontSize: '9px' }}>{labelMobile}</span>
                  <span className="text-gray-400" style={{ fontSize: '8px' }}>{statsTypes[value] || 0}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* PAR MARQUE */}
          <div>
            <h3 className="text-gray-700 text-xs font-bold uppercase tracking-wider mb-0.5 flex items-center gap-2">
              <span style={{ width: '3px', height: '12px', background: '#F5A623' }} className="rounded" />
              Par marque
            </h3>
 
            {/* Desktop : 1 ligne, toutes les marques */}
            <div className="hidden md:flex justify-between items-center">
              {marquesRapides.map((m) => <LogoMarque key={m.value} {...m} />)}
            </div>
 
            {/* ✅ 5 - Mobile : 9 premières marques sur 1 ligne */}
            <div className="md:hidden grid gap-0" style={{ gridTemplateColumns: 'repeat(9, 1fr)' }}>
              {marquesRapides.slice(0, 9).map((m) => (
                <Link key={m.value} to={`/annonces?marque=${encodeURIComponent(m.value)}`}
                  className="flex flex-col items-center justify-center py-0.5 transition group rounded-lg hover:bg-gray-50" title={m.value}>
                  <div className="w-8 h-8 flex items-center justify-center group-hover:scale-110 transition">
                    {logoErrors[m.value] ? (
                      <div style={{ background: m.color }} className="w-7 h-7 rounded-full flex items-center justify-center">
                        <span className="text-white font-black" style={{ fontSize: '10px' }}>{m.value.charAt(0)}</span>
                      </div>
                    ) : (
                      <img
                        src={m.logoUrl || `https://cdn.jsdelivr.net/gh/filippofilip95/car-logos-dataset@master/logos/optimized/${m.slug}.png`}
                        alt={m.value}
                        className="w-8 h-8 object-contain"
                        onError={() => setLogoErrors(prev => ({ ...prev, [m.value]: true }))}
                      />
                    )}
                  </div>
                  <span className="text-gray-400" style={{ fontSize: '8px' }}>{statsMarques[m.value] || 0}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
 
 
 
      {/* Bannière pub — après types/marques */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <BannierePublicitaire emplacement="homepage_top" className="h-20 md:h-24" />
      </div>
 
      {/* Dernières annonces */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-gray-900 text-xl font-black">Dernières annonces</h2>
            <div style={{ width: '50px', height: '3px', background: '#F5A623' }} className="mt-2 rounded" />
          </div>
          <Link to="/annonces" style={{ color: '#F5A623' }} className="text-sm font-semibold hover:opacity-80 transition">
            Voir tout →
          </Link>
        </div>
 
        <div className="flex gap-6">
          <div className="flex-1">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {annonces.map(annonce => (
                <Link key={annonce.id} to={`/annonces/${annonce.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition group border border-gray-100">
                  <div className="h-36 bg-gray-100 flex items-center justify-center relative overflow-hidden">
                    {annonce.est_mise_en_avant && (
                      <div style={{ background: '#F5A623' }}
                        className="absolute top-2 left-2 text-black text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 z-10">
                        <Star size={9} /> En avant
                      </div>
                    )}
                    {annonce.photo_principale ? (
                      <img src={getImageUrl(annonce.photo_principale)} alt={annonce.titre}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                    ) : (
                      <Car size={40} className="text-gray-300" />
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-gray-900 font-semibold text-sm truncate">{annonce.titre}</h3>
                    <p className="font-black text-base mt-1 text-gray-900">
                      {parseInt(annonce.prix).toLocaleString('fr-FR')} FCFA
                    </p>
                    <div className="flex gap-1 mt-1 text-xs text-gray-500 flex-wrap">
                      <span>{annonce.annee}</span><span>•</span>
                      <span>{annonce.kilometrage.toLocaleString()} km</span><span>•</span>
                      <span className="capitalize">{annonce.carburant}</span>
                    </div>
                    <p className="text-gray-400 text-xs mt-1 truncate">{annonce.localisation}</p>
                    <p className="text-gray-300 text-xs">Réf. JJC-{String(annonce.id).padStart(6, '0')}</p>
                  </div>
                </Link>
              ))}
            </div>
            {annonces.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Car size={60} className="mx-auto mb-4" />
                <p className="text-sm">Aucune annonce disponible pour le moment</p>
              </div>
            )}
          </div>
 
          <div className="w-72 flex-shrink-0 hidden md:block">
            <div className="sticky top-4">
              <BannierePublicitaire emplacement="homepage_sidebar" className="w-full rounded-2xl overflow-hidden" style={{ minHeight: '300px' }} />
            </div>
          </div>
        </div>
      </div>
 
      <div className="max-w-7xl mx-auto px-4 pb-4">
        <BannierePublicitaire emplacement="accueil_bottom" className="h-24" />
      </div>
 
      <footer style={{ background: '#0A0A0A' }} className="py-6 px-4 mt-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo4.jpg" alt="JJ Cars" className="h-10 w-auto object-contain" />
          <p className="text-gray-500 text-xs">© 2026 JJ Cars. Tous droits réservés.</p>
          <div className="flex gap-6 text-gray-500 text-xs flex-wrap justify-center">
            <Link to="/annonces" className="hover:text-yellow-400 transition">Annonces</Link>
            <Link to="/abonnements" className="hover:text-yellow-400 transition">Abonnements</Link>
            <Link to="/contact" className="hover:text-yellow-400 transition">Contact</Link>
            <a href="/privacy.html" className="hover:text-yellow-400 transition">Confidentialité</a>
            <a href="/terms.html" className="hover:text-yellow-400 transition">CGU</a>
            <a
              href="https://github.com/jeanjess/jjcars-app-releases/raw/main/jjcars-android.apk"
              rel="noopener"
              style={{ color: '#F5A623' }}
              className="font-semibold hover:opacity-80 transition flex items-center gap-1"
            >
              📱 Télécharger l'app Android
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
 
export default Accueil;